import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { IMAGES, SITE } from "@/config/site";
import Section, { RevealItem } from "@/components/Section";
import FloatingObjects from "@/components/FloatingObjects";
import PageHead from "@/components/PageHead";

export default function Projects() {
  const { t, lang } = useLang();
  const [filter, setFilter] = useState("all");

  const items = useMemo(() => {
    return t.projects.items.map((p, i) => ({
      ...p,
      img: IMAGES.projects[i % IMAGES.projects.length],
      id: i,
    }));
  }, [t.projects.items]);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((p) => p.cat === filter)),
    [items, filter]
  );

  const filters = [
    { k: "all", l: t.projects.filters.all },
    { k: "fabrication", l: t.projects.filters.fabrication },
    { k: "erection", l: t.projects.filters.erection },
    { k: "automation", l: t.projects.filters.automation },
    { k: "maintenance", l: t.projects.filters.maintenance },
  ];

  return (
    <>
      <PageHead customTitle={`${t.projects.title} · ${SITE.brand.en}`} customDesc={t.projects.subtitle} />

      <section className="relative pt-40 pb-12 overflow-hidden" data-testid="projects-hero">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES.fabrication} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy-900/85 to-navy-900" />
          <div className="absolute inset-0 grid-blueprint opacity-30" />
          <FloatingObjects density="low" />
        </div>
        <div className="container-pad">
          <div className="max-w-3xl">
            <div className="text-overline mb-4">{t.projects.overline}</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              {t.projects.title}
            </h1>
            <p className="mt-5 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
              {t.projects.subtitle}
            </p>
          </div>
        </div>
      </section>

      <Section testId="projects-grid" className="!py-12">
        <div className="container-pad">
          {/* Filter buttons */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="projects-filters">
            {filters.map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`projects-filter-${f.k}`}
                className={`px-5 py-2.5 text-xs md:text-sm font-bold tracking-wider uppercase transition-all border ${
                  filter === f.k
                    ? "bg-maroon-600 border-maroon-400 text-white"
                    : "bg-transparent border-white/15 text-white/75 hover:border-gold-500/60 hover:text-gold-500"
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>

          {/* Masonry grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[280px] md:auto-rows-[320px]">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, idx) => {
                const featured = idx % 5 === 0;
                return (
                  <motion.article
                    key={p.id}
                    layout
                    initial={{ opacity: 0, y: 30, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: (idx % 6) * 0.04 }}
                    data-testid={`project-card-${p.id}`}
                    className={`group relative overflow-hidden border border-white/10 hover:border-gold-500/40 transition-colors ${
                      featured ? "lg:row-span-2 lg:col-span-2" : ""
                    }`}
                  >
                    <img
                      src={p.img}
                      alt={p.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/55 to-navy-900/15" />
                    <div className="relative h-full flex flex-col justify-between p-6 md:p-7">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold-500/85">
                          {t.projects.categories[p.cat] || ""}
                        </span>
                        <span className="font-mono text-[10px] text-white/50">
                          EAH · {String(p.id + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-white text-2xl md:text-3xl leading-tight">
                          {p.title}
                        </h3>
                        <p className="mt-2 text-sm text-white/70 max-w-md leading-relaxed line-clamp-3">
                          {p.desc}
                        </p>
                        <button
                          type="button"
                          className="mt-4 inline-flex items-center gap-2 text-overline text-gold-500/80 hover:text-gold-500 transition-colors"
                          data-testid={`project-details-${p.id}`}
                        >
                          {t.projects.viewDetails} <ArrowUpRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold-500/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </div>

          <RevealItem className="mt-16">
            <div className="glass-strong p-8 md:p-10 text-center md:text-start">
              <div className="text-overline mb-2">EAH · {lang === "ar" ? "تواصل" : "Contact"}</div>
              <h3 className="font-heading font-bold text-white text-2xl md:text-3xl">
                {lang === "ar"
                  ? "هل لديك مشروع مماثل؟ نسعد بمناقشة التفاصيل."
                  : "Have a similar project? We'd love to discuss the details."}
              </h3>
            </div>
          </RevealItem>
        </div>
      </Section>
    </>
  );
}
